import { useTranslation } from "react-i18next";
import { useSetActiveBranch } from "../hooks/useBranching";
import { type Message, siblingLeafForOffset } from "../lib/thread";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";

/**
 * Branch navigator for an assistant message with sibling regenerations: ◄ n/m ►.
 * Each arrow switches the conversation's active branch to the neighbour's leaf,
 * which re-fetches the thread along that path.
 */
export function BranchSwitcher({
  message,
  conversationId,
}: {
  message: Message;
  conversationId: string;
}) {
  const { t } = useTranslation();
  const setBranch = useSetActiveBranch(conversationId);

  const count = message.variant_count ?? 0;
  if (count < 2) return null;

  const prevLeaf = siblingLeafForOffset(message, -1);
  const nextLeaf = siblingLeafForOffset(message, 1);
  const index = (message.variant_index ?? 0) + 1;

  const arrowClass =
    "grid size-6 place-items-center rounded text-ink outline-none transition-colors duration-150 ease-house hover:text-ink focus-visible:text-ink disabled:pointer-events-none disabled:opacity-30";

  return (
    <div className="inline-flex items-center gap-0.5">
      <button
        type="button"
        aria-label={t("common.previous")}
        disabled={!prevLeaf || setBranch.isPending}
        onClick={() => prevLeaf && setBranch.mutate(prevLeaf)}
        className={arrowClass}
      >
        <ChevronLeftIcon className="size-3.5" />
      </button>
      <span className="font-sans text-[11.5px] tabular-nums text-ink-muted">
        {index}/{count}
      </span>
      <button
        type="button"
        aria-label={t("common.next")}
        disabled={!nextLeaf || setBranch.isPending}
        onClick={() => nextLeaf && setBranch.mutate(nextLeaf)}
        className={arrowClass}
      >
        <ChevronRightIcon className="size-3.5" />
      </button>
    </div>
  );
}
